import { useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { toast } from 'react-toastify';
import { selectConsentEntry, selectProfile } from 'store/common/selectors/profile';
import { selectVerificationConfiguration } from 'store/common/selectors/general';
import { selectCameraPermissionGuideUsage } from 'features/govtId/state/selectors';
import { clearCameraPermissionGuideUsage } from 'features/govtId/state/slice';
import { useLazyGetProfileQuery, useSetConsentMutation } from 'api/profile';
import { useVerifyCaptchaMutation } from 'features/onboarding';
import { trackEventDuration } from 'lib/analytics';
import { getEnabledExtendedInputs, isReAuthenticateRoute, getErrorMessage } from 'utils/utilities';
import { prepareDashboardTitle, prepareDashboardDescription } from 'utils/formatContent';
import useRedirectToThankYouPage from 'hooks/useRedirectToThankYouPage';
import {
    MEDALLION_LINK_ERR_MSG,
    MESSAGES,
    REAUTHENTICATE_ROUTE,
    REAUTHENTICATE_ROUTES,
    eventNames,
} from 'utils/constants';
import validationSchema from './Consent.validation';
import { DISABLED_CAPTCHA_COMPANY_UUIDS } from 'features/common/helpers/constants';
import { selectReauthSessionId } from 'features/reauthenticate/state';
import { useUpdateProgressMutation } from 'features/reauthenticate/api';
import { selectSession } from 'features/onboarding/state/selectors';

export default function useConsentForm({ renderNextStep, isLastPoint }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const captchaRef = useRef(null);

    const profile = useSelector(selectProfile);
    const session = useSelector(selectSession);
    const verificationConfiguration = useSelector(selectVerificationConfiguration);
    const reAuthenticateSessionID = useSelector(selectReauthSessionId);
    const cachedGuideUsage = useSelector(selectCameraPermissionGuideUsage);
    const consentEntry = useSelector(selectConsentEntry);

    const [triggerGetProfile] = useLazyGetProfileQuery();
    const [setConsent] = useSetConsentMutation();
    const [verifyCaptcha] = useVerifyCaptchaMutation();
    const [updateProgress] = useUpdateProgressMutation();

    const reauthenticateRoute = isReAuthenticateRoute(location?.pathname);
    const disabledCaptcha = DISABLED_CAPTCHA_COMPANY_UUIDS.includes(profile?.company?.uuid);
    const shouldFetchProfile =
        session?.token && !profile?.company?.uuid && !profile?.loading && !profile?.error;

    useEffect(() => {
        // Only fetch profile if we have a token AND profile hasn't been loaded yet
        // This prevents duplicate API calls since profile is also fetched on token init

        if (shouldFetchProfile) {
            triggerGetProfile();
        }
    }, [triggerGetProfile, shouldFetchProfile]);

    useEffect(() => {
        const verificationToken = session?.token ?? reAuthenticateSessionID;

        //reset the camera permission usage cache every-time re-generate a token
        if (verificationToken && cachedGuideUsage?.flow) {
            const isTokenChanged = verificationToken !== cachedGuideUsage?.verificationUUID;

            isTokenChanged && dispatch(clearCameraPermissionGuideUsage());
        }
    }, [session?.token, reAuthenticateSessionID, cachedGuideUsage, dispatch]);

    useRedirectToThankYouPage(profile, isLastPoint, verificationConfiguration);

    const extendedChecksList = getEnabledExtendedInputs(profile?.extended_checks);
    const dashboardTitle = prepareDashboardTitle(reauthenticateRoute, extendedChecksList);
    const dashboardDesc = prepareDashboardDescription(reauthenticateRoute, extendedChecksList);
    const themeButton = profile?.company?.theme?.button?.variant;

    const handleUserFullNameInvalid = (error) => {
        const errorMessage = getErrorMessage({ response: { data: error?.data } });

        if (errorMessage.includes(MEDALLION_LINK_ERR_MSG.FULL_NAME_INVALID)) {
            toast.error(MESSAGES.USER_FULL_NAME_INVALID);
        }
    };

    const handleMedallionSubmit = async () => {
        try {
            const userDetails = profile?.user_details;

            if (!userDetails) return;

            const userFullName = [
                userDetails?.first_name,
                userDetails?.middle_name,
                userDetails?.last_name,
            ]
                .filter((x) => typeof x === 'string' && x.length > 0)
                .join(' ');

            trackEventDuration({
                event: eventNames.completed,
                uac: userDetails.uuid,
            });

            if (consentEntry) {
                return renderNextStep();
            }

            await setConsent({ userAccessCode: userDetails.uuid, name: userFullName }).unwrap();

            renderNextStep();
        } catch (error) {
            handleUserFullNameInvalid(error);
            throw error;
        }
    };

    const formik = useFormik({
        initialValues: { agree: reauthenticateRoute, captcha: '' },
        enableReinitialize: true,
        validationSchema: validationSchema(profile?.company?.uuid),
        onSubmit: async (values, { setErrors, setSubmitting }) => {
            try {
                if (reauthenticateRoute) {
                    if (isMobile) {
                        navigate(REAUTHENTICATE_ROUTES.CAPTURE_SELFIE);
                        updateProgress({
                            sessionId: reAuthenticateSessionID,
                            status: 'IN_PROGRESS',
                        });
                    } else {
                        navigate(`/${REAUTHENTICATE_ROUTE}/link`);
                    }
                    return;
                }

                setSubmitting(true);

                const validCaptcha = disabledCaptcha
                    ? true
                    : await verifyCaptcha(values.captcha).unwrap();

                if (validCaptcha) {
                    await handleMedallionSubmit();
                }

                setSubmitting(false);
            } catch {
                captchaRef.current?.reset();
                setSubmitting(false);
                setErrors({
                    captcha: 'Captcha verification failed. Please try again.',
                });
            }
        },
    });

    return {
        formik,
        captchaRef,
        profile,
        reauthenticateRoute,
        disabledCaptcha,
        dashboardTitle,
        dashboardDesc,
        themeButton,
    };
}
